import * as R from 'ramda'

import { HeaderWithSidebarIcon, StyledSubMenuItem } from '../'
import Popup from 'shared/components/Popup'
import React, { useCallback } from 'react'
import useAxios from 'hooks/useAxios'
import useSaveUserActivityLog, { FEEDBACK_VIEW_ACTION_NAME } from 'hooks/useSaveUserActivityLog'

import { Loader } from 'shared/components/Loader'
import { ViewLayoutModal } from 'components/ViewLayoutModal'
import { getDateRangeByDateFilterType } from 'shared/components/DateRangePicker/utils'
import styled from 'styled-components'
import { toast } from 'libs/toast'
import { useHistory } from 'react-router-dom'
import useModal from 'hooks/useModal'
import { useNavbarMenuContext } from '../context'
import useUser from 'hooks/useUser'
import useWorkspace from 'hooks/useWorkspace'

export default function FeedbackSubMenu() {
  const context = useNavbarMenuContext()
  const feedbackViewPages = getPageList('feedbackViewPages', context)
  const feedbackViewPagesLoading = getLoading('feedbackViewPagesLoading', context)
  const workspaceToken = useWorkspace()
  const history = useHistory()
  const saveUserActivityLog = useSaveUserActivityLog()
  const userInfo = useUser()
  const companyId = R.path(['selected_workspace', 'id'], userInfo)
  const { fetch: createFeedbackView, loading: createLoading } = useAxios({
    url: `dashboard/feedback-view-pages/`,
    method: 'post',
    skip: true,
  })

  const onCreateFeedbackView = useCallback(
    (requestPayload, closeEditView) => {
      const [startDate, endDate] = getDateRangeByDateFilterType(5)
      const data = {
        name: requestPayload.name,
        layout: requestPayload.layout,
        date_filter_type: 5,
        start_date: startDate,
        end_date: endDate,
        advanced_filters: {},
      }
      createFeedbackView({ data })
        .then((res) => {
          if (res.status === 201) {
            context.createContextData('feedback', res.data)
            history.push({
              pathname: `/${workspaceToken}/feedback/views/${res.data.token}/`,
              state: { feedbackViewName: res.data.name },
            })
            toast.success('New view created!')
            saveUserActivityLog({
              action: FEEDBACK_VIEW_ACTION_NAME.created_feedback_view,
              statusCode: res.status,
              requestBody: data,
              target: `com_${companyId}`,
            })
          } else {
            throw new Error('Failed to create view')
          }
        })
        .catch(() => {
          toast.error(
            'Uh oh. There was a problem while editing the view. Please try again or contact Simplesat support.'
          )
        })
        .finally(() => {
          closeEditView()
        })
    },
    [createFeedbackView, history, workspaceToken, companyId, saveUserActivityLog, context]
  )

  const {
    Modal: ModalNewFeedbackView,
    open: openNewFeedbackView,
    close: closeNewFeedbackView,
    isOpen: isOpenModalNewFeedbackView,
  } = useModal()

  return (
    <div className="ui borderless vertical menu fluid secondary !border-0 !m-0">
      <HeaderWithSidebarIcon>Feedback</HeaderWithSidebarIcon>
      <StyledSubMenuItem
        className="disable header-item item"
        to={{ pathname: `/${workspaceToken}/feedback/` }}
        isActive={(_, location) => location.pathname === `/${workspaceToken}/feedback/`}
      >
        <h5>All feedback</h5>
      </StyledSubMenuItem>

      <FavoriteFeedbackList workspaceToken={workspaceToken} />

      <h5 className="sub-menu-title item">Feedback</h5>
      {feedbackViewPagesLoading ? (
        <Loader className="ml-5 !z-[-1]" />
      ) : (
        <>
          {feedbackViewPages.map((feedbackView, index) => {
            return (
              <Popup.Truncated key={index} content={feedbackView.name}>
                <StyledSubMenuItem
                  className="sub-menu-item item"
                  to={{
                    pathname: `/${workspaceToken}/feedback/views/${feedbackView.token}/`,
                    state: { feedbackViewName: feedbackView.name },
                  }}
                >
                  {feedbackView.name}
                </StyledSubMenuItem>
              </Popup.Truncated>
            )
          })}
          <LinkButton
            onClick={(e) => {
              e.preventDefault()
              openNewFeedbackView()
            }}
          >
            <span>
              <i className="mx-1 far fa-plus" />
              New feedback view
            </span>
          </LinkButton>
          {isOpenModalNewFeedbackView && (
            <ModalNewFeedbackView size="tiny">
              <ViewLayoutModal
                modal={ModalNewFeedbackView}
                isCreateView={true}
                close={closeNewFeedbackView}
                onCreateView={onCreateFeedbackView}
                isDisabled={createLoading}
              />
            </ModalNewFeedbackView>
          )}
        </>
      )}
    </div>
  )
}

export function isFeedbackMenu(pathName) {
  return pathName.includes('/feedback/') || pathName.includes('/responses/')
}

function FavoriteFeedbackList({ workspaceToken }) {
  const context = useNavbarMenuContext()
  const favoriteFeedbackPages = getPageList('favoriteFeedbackPages', context).filter(
    (page) => page.type === 'feedback'
  )

  if (getLoading('favoriteFeedbackPagesLoading', context)) {
    return <Loader className="ml-5 !z-[-1]" />
  } else if (favoriteFeedbackPages.length === 0) {
    return null
  } else {
    return (
      <React.Fragment>
        <StyledSubMenuItem
          className="pointer-events-none sub-menu-title item disable"
          to="feedback/"
        >
          <span>Favorites</span>
        </StyledSubMenuItem>

        {favoriteFeedbackPages.map((feedbackView, index) => {
          return (
            <Popup.Truncated key={index} content={feedbackView.name}>
              <StyledSubMenuItem
                className="sub-menu-item item"
                to={{
                  pathname: `/${workspaceToken}/feedback/views/${feedbackView.token}/`,
                  state: { feedbackViewName: feedbackView.name },
                }}
              >
                {feedbackView.name}
              </StyledSubMenuItem>
            </Popup.Truncated>
          )
        })}
      </React.Fragment>
    )
  }
}

export function getPageList(field, context): [] {
  return R.path(['contextData', field, 'results'], context) || []
}

export function getLoading(field, context): boolean {
  return R.path(['contextData', field], context) || false
}

const LinkButton = styled.a`
  user-select: none;
  font-size: 14px;
  cursor: pointer;
  color: #9b9b9b;
  border: none;
  text-align: left;
  margin-left: 10px;

  &:hover {
    color: var(--blue-dark);
  }
  &.disable {
    &:hover {
      color: #9b9b9b;
    }
  }
`



// WEBPACK FOOTER //
// ./src/organisms/NavBar/FeedbackSubMenu/index.tsx